import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from './Alert';
import usuarioService from '../../services/UsuarioService';

const DEFAULT_ALERT_STATE = {
  show: false,
  title: '',
  message: '',
  type: 'error'
};

const DEFAULT_PASSWORD_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const UserSessionMenu = ({
  user,
  onLogout,
  extraItems = [],
  showStandaloneLogout = true,
  standaloneLogoutLabel = 'Salir',
  passwordLabel = 'Cambiar contraseña',
  userFallbackLabel = 'Usuario'
}) => {
  const menuRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState(DEFAULT_PASSWORD_FORM);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [canCloseModal, setCanCloseModal] = useState(true);
  const [alert, setAlert] = useState(DEFAULT_ALERT_STATE);

  const userName = useMemo(
    () => user?.displayName || user?.name || user?.email || userFallbackLabel,
    [user, userFallbackLabel]
  );
  const userEmail = user?.email || '';
  const visibleExtraItems = useMemo(
    () => (Array.isArray(extraItems) ? extraItems.filter((item) => item && !item.hidden) : []),
    [extraItems]
  );

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  const closePasswordModal = () => {
    if (!canCloseModal || passwordLoading) {
      return;
    }

    setShowPasswordModal(false);
    setPasswordForm(DEFAULT_PASSWORD_FORM);
    setCanCloseModal(true);
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordSubmit = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setAlert({
        show: true,
        message: 'Las contraseñas nuevas no coinciden',
        type: 'error',
        title: 'Error'
      });
      return;
    }

    if (!passwordForm.newPassword.trim()) {
      setAlert({
        show: true,
        message: 'La nueva contraseña no puede estar vacía',
        type: 'error',
        title: 'Error'
      });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setAlert({
        show: true,
        message: 'La nueva contraseña debe tener al menos 6 caracteres',
        type: 'error',
        title: 'Error'
      });
      return;
    }

    setPasswordLoading(true);
    setCanCloseModal(false);

    try {
      await usuarioService.updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );

      setShowPasswordModal(false);
      setPasswordForm(DEFAULT_PASSWORD_FORM);
      setAlert({
        show: true,
        message: 'Contraseña actualizada exitosamente',
        type: 'success',
        title: 'Éxito'
      });
    } catch (error) {
      setAlert({
        show: true,
        message: error.message || 'Error al actualizar la contraseña',
        type: 'error',
        title: 'Error'
      });
    } finally {
      setPasswordLoading(false);
      setCanCloseModal(true);
    }
  };

  const handlePasswordClick = () => {
    setShowMenu(false);
    setShowPasswordModal(true);
  };

  const handleLogoutClick = () => {
    setShowMenu(false);
    if (typeof onLogout === 'function') {
      onLogout();
    }
  };

  const handleExtraItemClick = (item) => {
    setShowMenu(false);
    if (typeof item?.onClick === 'function') {
      item.onClick();
    }
  };

  const passwordModalTarget = typeof document !== 'undefined'
    ? document.querySelector('.ctacte-app') || document.body
    : null;

  const passwordModal = (
    <div
      className="modal-overlay"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        closePasswordModal();
      }}
    >
      <div className="modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            <i className="fas fa-key"></i>
            {passwordLabel}
          </h3>
          <button
            type="button"
            className="modal-close-btn"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              closePasswordModal();
            }}
            disabled={!canCloseModal || passwordLoading}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="session-current-password">Contraseña actual</label>
            <input
              type="password"
              id="session-current-password"
              className="form-input"
              value={passwordForm.currentPassword}
              onChange={(event) => handlePasswordInputChange('currentPassword', event.target.value)}
              placeholder="Ingresa tu contraseña actual"
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="session-new-password">Nueva contraseña</label>
            <input
              type="password"
              id="session-new-password"
              className="form-input"
              value={passwordForm.newPassword}
              onChange={(event) => handlePasswordInputChange('newPassword', event.target.value)}
              placeholder="Ingresa tu nueva contraseña"
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="session-confirm-password">Repetir nueva contraseña</label>
            <input
              type="password"
              id="session-confirm-password"
              className="form-input"
              value={passwordForm.confirmPassword}
              onChange={(event) => handlePasswordInputChange('confirmPassword', event.target.value)}
              placeholder="Repite tu nueva contraseña"
              disabled={passwordLoading}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              closePasswordModal();
            }}
            disabled={!canCloseModal || passwordLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePasswordSubmit}
            disabled={passwordLoading}
          >
            {passwordLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Actualizando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Actualizar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {alert.show && (
        <Alert
          title={alert.title}
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(DEFAULT_ALERT_STATE)}
        />
      )}

      {showStandaloneLogout && (
        <button className="logout-btn-header" onClick={handleLogoutClick} title="Cerrar Sesión">
          <i className="fas fa-sign-out-alt"></i>
          <span className="logout-text">{standaloneLogoutLabel}</span>
        </button>
      )}

      <div className="user-menu" ref={menuRef}>
        <button
          type="button"
          className="user-menu-trigger"
          onClick={() => setShowMenu((prev) => !prev)}
        >
          <div className="user-avatar">
            <i className="fas fa-user"></i>
          </div>
          <i className="fas fa-chevron-down"></i>
        </button>

        {showMenu && (
          <div className="user-menu-dropdown">
            <div className="dropdown-header">
              <span className="user-name">{userName}</span>
              <span className="user-email">{userEmail}</span>
            </div>
            <div className="dropdown-divider"></div>
            <button type="button" className="dropdown-item" onClick={handlePasswordClick}>
              <i className="fas fa-key"></i>
              {passwordLabel}
            </button>
            {visibleExtraItems.map((item) => (
              <button
                key={item.key || item.label}
                type="button"
                className={`dropdown-item ${item.className || ''}`.trim()}
                onClick={() => handleExtraItemClick(item)}
              >
                {item.icon && <i className={item.icon}></i>}
                {item.label}
              </button>
            ))}
            <div className="dropdown-divider"></div>
            <button type="button" className="dropdown-item logout-item" onClick={handleLogoutClick}>
              <i className="fas fa-sign-out-alt"></i>
              Cerrar Sesión
            </button>
          </div>
        )}
      </div>

      {showPasswordModal && passwordModalTarget && createPortal(passwordModal, passwordModalTarget)}
    </>
  );
};

export default UserSessionMenu;
